import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useShapesTemplateList} from '../../../hooks/useShapesTemplateList'
import {useDraftShapes} from '../../../hooks/useDraftShapes'
import {usePagination} from '../../../hooks/usePagination'
import {restoreDraftImages} from '../../../utils/restoreDraftImages'
import DeleteShapeConfirmModal from './DeleteShapeConfirmModal'
import SavedShapesSection from './SavedShapesSection'
import BasicShapesSection from './BasicShapesSection'
import DraftShapesSection from './DraftShapesSection'
import {SHAPE_PATHS} from '../../../../../../helpers/shapes/shapePaths'
import {addNode} from '../../../../editorActions'
import {createNodeAtViewportCenter} from '../../../../utils/placeNodeAtViewportCenter'
import {deleteDesignLibraryTemplate} from '../../../../../../services/ArtworkTemplateServices'
import {toaster} from '../../../../../../shared/PFToast'
import {NODE_TYPES} from '../../../../constants'

const BASIC_SHAPES = Object.entries(SHAPE_PATHS).map(([shapeType, def]) => ({
    shapeType,
    data: def.data,
    rawWidth: def.rawWidth,
    rawHeight: def.rawHeight,
    label: shapeType
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' '),
}))

const BASIC_SHAPE_LIMIT = 9

const ShapesPanel = ({runtime, onSelectDraft, onSelectShape, savedShapesRefreshKey}) => {
    const [searchQuery, setSearchQuery] = useState('')
    const [activeFilter, setActiveFilter] = useState('all')
    const searchTimeoutRef = useRef(null)
    const [deleteConfirm, setDeleteConfirm] = useState(null)

    const {items, setItems, limit, loading, error, pagination: savedPagination, setFilters} = useShapesTemplateList({
        limit: 9,
        refreshKey: savedShapesRefreshKey,
    })

    const {drafts, loading: draftsLoading, deleteDraft} = useDraftShapes()
    const draftLimit = 9

    const basicPagination = usePagination({
        totalItems: BASIC_SHAPES.length,
        limit: BASIC_SHAPE_LIMIT,
    })
    const draftPagination = usePagination({
        totalItems: drafts.length,
        limit: draftLimit,
        resetKey: drafts.length,
    })
    const handleAddBasicShape = useCallback(
        (shapeType) => {
            if (!runtime?.dispatch) return
            const shapeDef = SHAPE_PATHS[shapeType]
            if (!shapeDef) return
            const id = Date.now().toString(36) + Math.random().toString(36).slice(2)
            const node = createNodeAtViewportCenter({
                type: NODE_TYPES.SVG_SHAPE,
                geometry: {
                    x: 100,
                    y: 100,
                    width: shapeDef.rawWidth,
                    height: shapeDef.rawHeight,
                    rotation: 0,
                },
                // Persist shapeType inside style so it survives serialize/persistence.
                style: {fill: '#000000', stroke: '', strokeWidth: 0, shapeType},
            })(runtime.getViewportCenter?.())({id})
            runtime.dispatch(addNode(node))
        },
        [runtime],
    )

    const handleSelectShape = useCallback(
        async (template) => {
            if (!runtime?.loadDocument) return
            const raw = template.data
            if (raw == null) return
            const doc = typeof raw === 'string' ? JSON.parse(raw) : raw
            const meta = {
                id: template._id,
                name: template.name,
                description: template.description,
            }
            await runtime.loadDocument(doc, meta)
            onSelectShape?.()
        },
        [runtime, onSelectShape],
    )

    const handleSelectDraft = useCallback(
        async (draft) => {
            if (!runtime?.loadDocument || !draft?.document) return
            await runtime.loadDocument(draft.document)
            await restoreDraftImages(runtime, draft.id)
            onSelectDraft?.(draft.id)
        },
        [runtime, onSelectDraft],
    )

    const handleSearchChange = useCallback(
        (e) => {
            const value = e.target.value
            setSearchQuery(value)
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
            searchTimeoutRef.current = setTimeout(() => {
                setFilters({name: value.trim()})
            }, 400)
        },
        [setFilters],
    )

    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteConfirm) return
        if (deleteConfirm.type === 'draft') {
            deleteDraft(deleteConfirm.item.id)
        }
        if (deleteConfirm.type === 'saved') {
            const deleteShapeTemplate = deleteDesignLibraryTemplate
            const {success, message} = await deleteShapeTemplate(deleteConfirm.item._id)
            if (!success) {
                toaster({type: 'error', message: message || 'Failed to delete shape'})
                return
            }
            toaster({type: 'success', message: 'Shape deleted successfully'})
            setItems((prevItems) => prevItems.filter((item) => item._id !== deleteConfirm.item._id))
        }
    }, [deleteConfirm, deleteDraft])

    const closeDeleteModal = useCallback(() => {
        setDeleteConfirm(null)
    }, [])

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
        }
    }, [])

    const pagedBasicShapes = BASIC_SHAPES.slice(basicPagination.startIndex, basicPagination.endIndex)
    const pagedDrafts = drafts.slice(draftPagination.startIndex, draftPagination.endIndex)

    const showSaved = activeFilter === 'all' || activeFilter === 'yours'
    const showBasic = activeFilter === 'all' || activeFilter === 'basics'

    return (
        <section className="SidebarPanel" aria-label="Shapes">
            <header className="SidebarPanel__Header">
                <h2 className="SidebarPanel__Title">Shapes</h2>
            </header>

            <div className="SidebarPanel__Content">
                {/* Search input */}
                <div className="SidebarPanel__SearchWrap">
                    <div className="SidebarPanel__SearchField">
                        <i className="fas fa-search SidebarPanel__SearchIcon" />
                        <input
                            type="text"
                            className="SidebarPanel__SearchInput"
                            placeholder="Search shape..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            aria-label="Search shapes"
                        />
                    </div>
                </div>
                {/* Filter shape block */}
                <div className="SidebarPanel__FilterRow">
                    {[
                        {key: 'all', label: 'All shapes'},
                        {key: 'yours', label: 'Your shapes'},
                        {key: 'basics', label: 'Basic shapes'},
                    ].map(({key, label}) => (
                        <button
                            key={key}
                            type="button"
                            className={`SidebarPanel__FilterTab${activeFilter === key ? ' SidebarPanel__FilterTab--active' : ''}`}
                            onClick={() => setActiveFilter(key)}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* Saved Shapes block */}
                {showSaved && (
                    <SavedShapesSection
                        loading={loading}
                        error={error}
                        items={items}
                        skeletonCount={limit}
                        pagination={savedPagination}
                        onSelectShape={handleSelectShape}
                        onDeleteShape={(template) => setDeleteConfirm({type: 'saved', item: template})}
                    />
                )}

                {/* Basic Shapes block */}
                {showBasic && (
                    <BasicShapesSection
                        items={pagedBasicShapes}
                        pagination={basicPagination}
                        onAddShape={handleAddBasicShape}
                    />
                )}

                {/* Draft Shapes block */}
                {showSaved && (draftsLoading || drafts.length > 0) && (
                    <DraftShapesSection
                        loading={draftsLoading}
                        items={pagedDrafts}
                        skeletonCount={draftLimit}
                        pagination={draftPagination}
                        onSelectDraft={handleSelectDraft}
                        onDeleteDraft={(draft) => setDeleteConfirm({type: 'draft', item: draft})}
                    />
                )}
            </div>

            {deleteConfirm && (
                <DeleteShapeConfirmModal
                    isOpen={!!deleteConfirm}
                    toggle={closeDeleteModal}
                    type={deleteConfirm.type}
                    itemName={deleteConfirm.type === 'saved' ? deleteConfirm.item.name : undefined}
                    onConfirm={handleDeleteConfirm}
                />
            )}
        </section>
    )
}

export default ShapesPanel
